import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Modal, ModalProps, styled } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import ArrowIcon from "../../assets/Icons/ArrowIcon";
import BannerIcon from "../../assets/Icons/BannerIcon";
import LikeIcon from "../../assets/Icons/LikeIcon";
import ProfileIcon from "../../assets/Icons/ProfileIcon2";
import RoundRemoveIcon from "../../assets/Icons/RoundRemoveIcon";
import ShareIcon from "../../assets/Icons/ShareIcon";
import {
  removeProfileImage,
  setBannerImage,
  setProfileImageUrl,
} from "../../firebase/user";
import { theme } from "../../mui-customs/Theme";
import { RootState } from "../../redux/store";
import Button from "../Button";
import PhotoDeleteConformationModal from "../PhotoDeleteConformationModal";
import SquareIconButton from "../SquareIconButton";
import Typography from "../Typography";

type PhotoDetailsModalProps = Omit<ModalProps, "children"> & {
  handleClose: () => void;
  handleNextImage?: () => void;
  handlePreviousImage?: () => void;
  handleImageRemove?: () => void;
  handleImageShare?: () => void;
  handleImageLike?: () => void;
  handleImageChoose?: (url: string) => void;
  imgUrl?: string;
  imgRef?: string;
  readOnly: boolean;
};

const StyledIconButton = styled(IconButton)`
  background-color: rgba(0, 0, 0, 0.36);
  backdrop-filter: blur(5px);
  & img {
    filter: brightness(2);
  }
  :hover {
    background-color: rgba(0, 0, 0, 0.533);
  }
`;

export default function PhotoDetailsModal({
  open,
  handleClose,
  imgUrl = "",
  imgRef,
  handleNextImage,
  handlePreviousImage,
  handleImageRemove,
  handleImageShare,
  handleImageLike,
  handleImageChoose,
  readOnly,
  ...props
}: PhotoDetailsModalProps) {
  const [isDisplayModal, setIsDisplayModal] = useState(false);
  const userUid = useSelector((state: RootState) => state.user.user.uid);
  const userData = useSelector((state: RootState) => state.user.user);
  const [isSettingBannerImage, setIsSettingBannerImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [settingProfileImage, setSettingProfileImage] = useState(false);

  const handleSettingBannerImage = async () => {
    if (userUid && imgUrl) {
      setIsSettingBannerImage(true);
      await setBannerImage(imgUrl, userUid);
      toast.success("Banner image successfully added.");
      setIsSettingBannerImage(false);
    }
  };

  const handleClickOnDeleteImage = async () => {
    setIsDeletingImage(true);
    setIsDisplayModal(false);
    handleImageRemove && (await handleImageRemove());
    setIsDeletingImage(false);
  };

  const handleSetProfilePicture = async () => {
    if (userUid) {
      setSettingProfileImage(true);
      await setProfileImageUrl(imgUrl, userUid);
      await removeProfileImage(userUid);
      setSettingProfileImage(false);
    }
  };

  const handleShareButtonClick = async () => {
    try {
      if (imgUrl) {
        const shareData = {
          url: imgUrl,
        };
        if (navigator.share && navigator.canShare(shareData)) {
          navigator.share(shareData);
        } else {
          navigator.clipboard.writeText(imgUrl);
          toast.info("Url copied to clipboard");
        }
      }
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <Modal
      sx={{ display: "grid", placeItems: "center" }}
      open={open}
      onClose={handleClose}
      {...props}
    >
      <Box
        sx={{
          backgroundColor: theme.whiteBg.WBG1,
          padding: { xs: 2.5, sm: 4 },
          borderRadius: 2,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: { xs: "10px", md: "30px" },
              right: { xs: "10px", md: "30px" },
            }}
            onClick={handleClose}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Typography
            variant="btnMedium"
            sx={{
              color: `${theme.black[800]} !important`,
              width: "fit-content",
            }}
          >
            Photo view
          </Typography>
        </Box>
        <Box
          sx={{
            position: "relative",
            borderRadius: { xs: 0, sm: 2 },
            margin: { xs: "20px 0px 25px 0px", sm: "30px 0px 25px 0px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={imgUrl}
            sx={{ maxHeight: { xs: "55vh", md: "60vh" }, maxWidth: "85vw" }}
          />
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              gap: 3,
              bottom: "16px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {handlePreviousImage && (
              <StyledIconButton onClick={handlePreviousImage}>
                <ArrowIcon />
              </StyledIconButton>
            )}
            {handleNextImage && (
              <StyledIconButton
                onClick={handleNextImage}
                sx={{ transform: "rotate(180deg)" }}
              >
                <ArrowIcon />
              </StyledIconButton>
            )}
          </Box>
        </Box>
        {readOnly ? (
          <Box
            sx={{
              display: "flex",
              mx: { xs: "30px", sm: "0px" },
              justifyContent: "space-evenly",
            }}
          >
            {handleImageChoose && (
              <Button
                onClick={() => {
                  handleImageChoose(imgUrl);
                }}
              >
                Choose and edit
              </Button>
            )}
            {handleImageLike && (
              <SquareIconButton Icon={LikeIcon} title="Likes" />
            )}
            {handleImageShare && (
              <SquareIconButton Icon={ShareIcon} title="Share pic" />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              mx: { xs: "20px", sm: "0px" },
              gap: { md: 3 },
              justifyContent: { xs: "space-between", md: "center" },
            }}
          >
            <SquareIconButton
              Icon={BannerIcon}
              loading={isSettingBannerImage}
              title="Set Banner"
              onClick={handleSettingBannerImage}
            />
            <SquareIconButton
              onClick={handleShareButtonClick}
              Icon={ShareIcon}
              title="Share pic"
            />
            <SquareIconButton
              loading={settingProfileImage}
              onClick={handleSetProfilePicture}
              Icon={ProfileIcon}
              title="Set profile"
            />
            <SquareIconButton
              loading={isDeletingImage}
              onClick={() => {
                setIsDisplayModal(true);
              }}
              Icon={RoundRemoveIcon}
              title="Delete pic"
            />
          </Box>
        )}
        <PhotoDeleteConformationModal
          open={isDisplayModal}
          onClose={() => {
            setIsDisplayModal(false);
          }}
          handleDelete={handleClickOnDeleteImage}
        />
      </Box>
    </Modal>
  );
}
